<script setup lang="ts">
import { champNomAffiche } from "~/utils/champNomDonateur";

const { t } = useI18n();

interface CustomField {
  type: 'fullname' | 'email' | 'message';
  required: boolean;
  label: string;
}

const props = defineProps<{
  donorEmail: string;
  donorName: string;
  isAnonymous: boolean;
  touched: { email: boolean; name: boolean };
  errors: { email: string | null; name: string | null };
  message?: string;
  showMessageField?: boolean;
  customFields?: CustomField[] | null;
  customFieldValues?: Record<string, string>;
}>();

const emit = defineEmits<{
  "update:donorEmail": [value: string];
  "update:donorName": [value: string];
  "update:isAnonymous": [value: boolean];
  "update:message": [value: string];
  "update:customFieldValues": [value: Record<string, string>];
  validateEmail: [];
  validateName: [];
}>();

// Quand customFields est null (pas configuré), tout est affiché par défaut
// Quand customFields est un array, seuls les champs listés sont affichés
const showFullname = computed(() => champNomAffiche(props.customFields));

const showEmail = computed(() => {
  if (!props.customFields) return true;
  return props.customFields.some(f => f.type === 'email');
});

const updateCustomFieldValue = (type: string, value: string) => {
  emit("update:customFieldValues", {
    ...(props.customFieldValues || {}),
    [type]: value,
  });
};
</script>

<template>
  <div class="space-y-4">
    <!-- Groupe de champs fusionnés style Stripe.
         Le wrapper porte la border au repos + un ring de la même couleur au
         focus-within (= quand un input interne a le focus). On retire le ring
         des inputs internes pour ne PAS avoir 2 anneaux superposés : le ring
         du wrapper englobe les 2 inputs comme une seule unité, exactement
         comme Stripe Elements. -->
    <div
      v-if="showFullname || showEmail"
      :class="[
        'rounded-lg border overflow-hidden transition-all duration-200 focus-within:ring-1',
        (touched.email && errors.email) || (touched.name && errors.name)
          ? 'border-red-400 dark:border-red-500 focus-within:ring-red-500'
          : 'border-gray-300 dark:border-gray-600 focus-within:ring-blue-500 focus-within:border-blue-500',
      ]"
    >
      <!-- Nom complet (masqué si don anonyme) -->
      <template v-if="showFullname && !isAnonymous">
        <input
          :value="donorName"
          type="text"
          :placeholder="t('don.formulaireDonateur.nomComplet')"
          autocomplete="name"
          class="w-full px-3 py-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:outline-none border-0 text-base"
          @input="
            emit('update:donorName', ($event.target as HTMLInputElement).value)
          "
          @blur="emit('validateName')"
        />
        <!-- Ligne de séparation (seulement si email aussi affiché) -->
        <div v-if="showEmail" class="border-t border-gray-300 dark:border-gray-600" />
      </template>
      <!-- Email -->
      <input
        v-if="showEmail"
        id="donor-email"
        :value="donorEmail"
        name="email"
        type="email"
        :placeholder="t('don.formulaireDonateur.adresseEmail')"
        autocomplete="email"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        class="w-full px-3 py-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 outline-none focus:outline-none border-0 text-base"
        @input="
          emit('update:donorEmail', ($event.target as HTMLInputElement).value)
        "
        @blur="emit('validateEmail')"
      />
    </div>

    <!-- Messages d'erreur -->
    <div
      v-if="(touched.name && errors.name) || (touched.email && errors.email)"
      class="space-y-1"
    >
      <div
        v-if="touched.name && errors.name"
        class="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
      >
        <UIcon name="i-lucide-alert-circle" class="w-3.5 h-3.5" />
        {{ errors.name }}
      </div>
      <div
        v-if="touched.email && errors.email"
        class="text-xs text-red-600 dark:text-red-400 flex items-center gap-1"
      >
        <UIcon name="i-lucide-alert-circle" class="w-3.5 h-3.5" />
        {{ errors.email }}
      </div>
    </div>

    <!-- Don anonyme (masqué si nom complet et email sont désactivés) -->
    <label v-if="showFullname || showEmail" class="flex items-center gap-2 cursor-pointer">
      <UCheckbox
        :model-value="isAnonymous"
        color="info"
        @update:model-value="emit('update:isAnonymous', $event as boolean)"
      />
      <span class="text-sm text-gray-700 dark:text-gray-300">
        {{ t('don.formulaireDonateur.donAnonyme') }}
      </span>
      <!-- ClientOnly : UTooltip (Reka) téléporte → hydration mismatch sur la
           page de don (cassait l'init Stripe). Client-only + fallback icône. -->
      <ClientOnly>
        <UTooltip
          :delay-duration="0"
          :content="{ side: 'top', align: 'start', sideOffset: 8 }"
          :ui="{
            content:
              '!h-auto bg-white text-gray-700 shadow-lg border border-gray-200 rounded-lg p-3 text-xs w-52 whitespace-normal ring-0',
          }"
        >
          <UIcon
            name="i-lucide-info"
            class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors"
            @click.stop
          />
          <template #content>
            {{ t('don.formulaireDonateur.infobulleAnonyme') }}
          </template>
        </UTooltip>
        <template #fallback>
          <UIcon
            name="i-lucide-info"
            class="w-4 h-4 text-gray-400 cursor-help"
          />
        </template>
      </ClientOnly>
    </label>

    <!-- Champ message personnalisé -->
    <template v-if="customFields && customFields.length > 0">
      <div
        v-for="field in customFields"
        :key="field.type"
      >
        <div v-if="field.type === 'message'">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {{ field.label || t('don.formulaireDonateur.messageLabelDefaut') }}
            <span v-if="field.required" class="text-red-500">*</span>
          </label>
          <UTextarea
            :model-value="customFieldValues?.[field.type] || ''"
            :placeholder="t('don.formulaireDonateur.placeholderMessage')"
            :rows="2"
            class="w-full [&_textarea]:resize-none"
            :ui="{ base: 'border border-gray-300 dark:border-gray-600 rounded-lg' }"
            @update:model-value="updateCustomFieldValue(field.type, $event as string)"
          />
        </div>
      </div>
    </template>

    <!-- Message optionnel (mobile uniquement — desktop l'affiche dans la colonne gauche)
         border et padding-x retirés du wrapper (l'input gère sa propre border).
         Padding interne `p-px` sur l'UTextarea pour offrir un léger respire
         visuel au focus ring sans coller aux bords du wrapper. -->
    <div v-if="showMessageField" class="lg:hidden mt-2">
      <label class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
        {{ t('don.formulaireDonateur.messageOptionnel') }}
      </label>
      <UTextarea
        :model-value="message"
        :placeholder="t('don.formulaireDonateur.placeholderMessageEncouragement')"
        :rows="2"
        class="w-full"
        :ui="{
          base: 'rounded-2xl! px-4 py-3 leading-relaxed bg-white! text-gray-900! placeholder:text-gray-400! ring-1 ring-gray-200! focus:ring-2 focus:ring-blue-500!',
        }"
        @update:model-value="emit('update:message', $event as string)"
      />
    </div>
  </div>
</template>
